import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  processCheckout,
  fetchUserCarts,
  recalculateCartTotals,
  getUserShippingAddress,
  getUserPaymentMethodHint
} from '../api';
import vpStyles from './CartPageVision.module.css';
import authStyles from '../components/AuthForm.module.css';
import { LockClosedIcon, CreditCardIcon, MapPinIcon, ShoppingCartIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '../context/AuthContext';

const formatCardNumber = (value) => {
  const cleaned = value.replace(/\D/g, '');
  const matches = cleaned.match(/.{1,4}/g);
  return matches ? matches.join(' ') : '';
};

const formatExpiryDate = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 3) {
        return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}`;
    }
    return cleaned;
};

function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [cartDetails, setCartDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [savePaymentInfo, setSavePaymentInfo] = useState(true);
  const [savedPaymentHint, setSavedPaymentHint] = useState(null);

  const [formData, setFormData] = useState({
    firstName: '', lastName: '', address: '', city: '', state: '', zip_code: '',
  });

  const cartIds = useMemo(() => location.state?.cartIds || [], [location.state]);

  const stripe = useStripe();
  const elements = useElements();
  const [isStripeReady, setIsStripeReady] = useState(false);

  const fetchCheckoutData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setSavedPaymentHint(null);
    try {
      await recalculateCartTotals(cartIds);

      const userCartsResponse = await fetchUserCarts();
      const relevantCarts = userCartsResponse.data.filter(cart => cartIds.includes(cart.cart_id));
      if (relevantCarts.length === 0) throw new Error("Selected carts not found.");
      const allItems = relevantCarts.flatMap(cart => cart.items || []);
      setCartDetails(allItems);

      const shippingHint = await getUserShippingAddress();
      if (shippingHint.data && Object.keys(shippingHint.data).length > 0) {
        setFormData(prev => ({
          ...prev,
          firstName: shippingHint.data.firstName || '',
          lastName: shippingHint.data.lastName || '',
          address: shippingHint.data.address || '',
          city: shippingHint.data.city || '',
          state: shippingHint.data.state || '',
          zip_code: shippingHint.data.zip_code || '',
        }));
      }

      const paymentHint = await getUserPaymentMethodHint();
      if (paymentHint.data && paymentHint.data.cardLastFour) {
        setSavedPaymentHint(paymentHint.data);
        setSavePaymentInfo(false);
      } else {
        setSavePaymentInfo(true);
      }

    } catch (err) {
      console.error('Error fetching checkout data:', err);
      setError(err.message || 'Failed to load checkout details. Please go back and try again.');
      setCartDetails([]);
    } finally {
      setIsLoading(false);
    }
  }, [cartIds]);

  useEffect(() => {
    if (!cartIds.length) {
      navigate('/cart');
      return;
    }
    if (user) {
       fetchCheckoutData();
    }
  }, [cartIds, navigate, fetchCheckoutData, user]);

  const subtotal = useMemo(() => {
    return cartDetails.reduce((sum, item) => {
      return sum + (parseFloat(item.price || 0) * (parseInt(item.quantity || 1)));
    }, 0);
  }, [cartDetails]);

  const shippingCost = subtotal > 0 ? 5.00 : 0;
  const taxes = subtotal * 0.08;

  const grandTotal = useMemo(() => subtotal + shippingCost + taxes, [subtotal, shippingCost, taxes]);

  const handleAddressInputChange = (e) => {
     const { name, value } = e.target;
     let formattedValue = value;
     if (name === 'zip_code') {
        formattedValue = value.replace(/\D/g, '').slice(0, 5);
     }
     setFormData(prev => ({ ...prev, [name]: formattedValue }));
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    setError(null);

    if (!stripe || !elements) {
      setError("Payment processing is not ready. Please wait a moment and try again.");
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
        setError("Payment details component is not available. Please refresh the page.");
        return;
    }

    if (!isStripeReady) {
       setError("Please complete the card details accurately.");
       return;
    }

    setIsProcessing(true);

    const requiredFields = ['firstName', 'lastName', 'address', 'city', 'state', 'zip_code'];
    const missingFields = requiredFields.filter(field => !formData[field]?.trim());
    if (missingFields.length > 0) {
      setError(`Please fill in all required address fields: ${missingFields.join(', ')}`);
      setIsProcessing(false);
      return;
    }

    try {
      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
           name: `${formData.firstName} ${formData.lastName}`,
           email: user?.email,
           address: {
              line1: formData.address,
              city: formData.city,
              state: formData.state,
              postal_code: formData.zip_code,
           },
        },
      });

      if (paymentMethodError) {
        throw new Error(paymentMethodError.message || 'Failed to create payment method. Please check card details.');
      }
      console.log("PaymentMethod created:", paymentMethod.id);

      const checkoutPayload = {
        cartIds,
        shippingInfo: {
           firstName: formData.firstName,
           lastName: formData.lastName,
           address: formData.address,
           city: formData.city,
           state: formData.state,
           zip_code: formData.zip_code,
        },
        totalAmount: grandTotal,
        paymentMethodId: paymentMethod.id,
        saveInfo: savePaymentInfo
      };

      console.log("Submitting checkout payload:", checkoutPayload);
      const response = await processCheckout(checkoutPayload);
      console.log("Checkout response:", response);

      if (response.data?.success) {
        console.log("Checkout successful on backend. Clearing card element.");
        cardElement.clear();

        const orderId = response.data.orderIds?.[0];
        const vendorCount = 1;

        if (orderId) {
            navigate('/order-processing', { state: { orderId: orderId, vendorCount: vendorCount } });
        } else {
            console.error("Checkout successful but no Order ID received.");
            setError("Checkout complete, but there was an issue retrieving your order details.");
        }

      } else if (response.data?.error === 'requires_action' && response.data?.clientSecret) {
          console.log("Payment requires action. Confirming card payment with client secret...");
          const { error: confirmError } = await stripe.confirmCardPayment(
              response.data.clientSecret
          );

          if (confirmError) {
              console.error("Card confirmation error:", confirmError);
              throw new Error(confirmError.message || "Failed to confirm payment. Please try again.");
          } else {
              console.log("Card payment confirmed after action.");
              setError("Payment requires confirmation steps which are not fully handled yet. Please try placing the order again.");
          }
      } else {
          throw new Error(response.data?.message || 'Checkout failed. Please try again.');
      }

    } catch (err) {
      console.error('Checkout submission error:', err);
      setError(err.message || 'An unexpected error occurred during checkout.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center items-center min-h-screen text-gray-500">Loading Checkout...</div>;
  }

  if (error && cartDetails.length === 0) {
      return (
          <div className={`${vpStyles.pageContainer} text-center py-20`}>
              <h1 className="text-2xl font-semibold mb-4 text-red-600">Error Loading Checkout</h1>
              <p className="text-gray-600 mb-6">{error}</p>
              <button onClick={() => navigate('/cart')} className={vpStyles.primaryButton}>Return to Cart</button>
          </div>
      );
  }

  return (
    <div className={`${vpStyles.pageContainer} max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16`}>
      <h1 className={`${vpStyles.pageTitle} text-3xl md:text-4xl font-semibold text-center mb-10 md:mb-14`}>Checkout</h1>

      <form onSubmit={handleCheckout} className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">

        <div className="lg:col-span-2 space-y-8">

          <div className={vpStyles.cartPanel}>
            <h2 className={`${vpStyles.panelTitle} p-5 border-b border-[var(--vp-border-color)] flex items-center gap-2`}>
                <MapPinIcon className="w-5 h-5 text-gray-500" /> Shipping Address
            </h2>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="firstName" className={authStyles.inputLabel}>First Name</label>
                <input type="text" name="firstName" id="firstName" value={formData.firstName} onChange={handleAddressInputChange} required className={authStyles.inputField} />
              </div>
              <div>
                <label htmlFor="lastName" className={authStyles.inputLabel}>Last Name</label>
                <input type="text" name="lastName" id="lastName" value={formData.lastName} onChange={handleAddressInputChange} required className={authStyles.inputField} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="address" className={authStyles.inputLabel}>Address</label>
                <input type="text" name="address" id="address" value={formData.address} onChange={handleAddressInputChange} required className={authStyles.inputField} />
              </div>
              <div>
                <label htmlFor="city" className={authStyles.inputLabel}>City</label>
                <input type="text" name="city" id="city" value={formData.city} onChange={handleAddressInputChange} required className={authStyles.inputField} />
              </div>
              <div>
                <label htmlFor="state" className={authStyles.inputLabel}>State / Province</label>
                <input type="text" name="state" id="state" value={formData.state} onChange={handleAddressInputChange} required className={authStyles.inputField} />
              </div>
              <div>
                <label htmlFor="zip_code" className={authStyles.inputLabel}>ZIP / Postal Code</label>
                <input type="text" name="zip_code" id="zip_code" value={formData.zip_code} onChange={handleAddressInputChange} required inputMode="numeric" className={authStyles.inputField} />
              </div>
            </div>
          </div>

          <div className={vpStyles.cartPanel}>
             <h2 className={`${vpStyles.panelTitle} p-5 border-b border-[var(--vp-border-color)] flex items-center gap-2`}>
                 <CreditCardIcon className="w-5 h-5 text-gray-500" /> Payment Details
             </h2>
            <div className="p-6 space-y-5">
               {savedPaymentHint && (
                 <div className="p-3 border border-green-300 bg-green-50 rounded-md flex items-center text-sm text-green-800">
                   <CheckCircleIcon className="w-5 h-5 mr-2 text-green-600 flex-shrink-0" />
                   <span>
                     Using saved card: <span className="font-medium">{savedPaymentHint.cardBrand?.toUpperCase()}</span> ending in <span className="font-medium">{savedPaymentHint.cardLastFour}</span>.
                     Enter new details below to update.
                   </span>
                 </div>
               )}

               <div className="p-3 border border-gray-300 rounded-md bg-white">
                   <CardElement
                     options={{
                        style: {
                           base: {
                              fontSize: '16px',
                              color: '#424770',
                              '::placeholder': { color: '#aab7c4' },
                              iconColor: '#6772e5',
                           },
                           invalid: {
                              color: '#dc3545',
                              iconColor: '#dc3545',
                           },
                        },
                        hidePostalCode: true
                     }}
                     onChange={(event) => {
                        setError(event.error ? event.error.message : null);
                        setIsStripeReady(!event.empty && event.complete && !event.error);
                     }}
                   />
               </div>

               <div className="pt-2">
                  <label className="flex items-center text-sm text-gray-700 cursor-pointer">
                      <input
                          type="checkbox"
                          checked={savePaymentInfo}
                          onChange={(e) => setSavePaymentInfo(e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 mr-2 shadow-sm"
                          aria-describedby="save-info-description"
                      />
                      <span id="save-info-description">
                         Save this payment information for next time
                      </span>
                  </label>
              </div>
            </div>
          </div>

        </div>

        <div className="lg:col-span-1">
          <div className={`${vpStyles.cartPanel} sticky top-8`}>
             <h2 className={`${vpStyles.panelTitle} p-5 border-b border-[var(--vp-border-color)] flex items-center gap-2`}>
                <ShoppingCartIcon className="w-5 h-5 text-gray-500" /> Order Summary
             </h2>

             <div className="p-6 max-h-60 overflow-y-auto space-y-4 divide-y divide-gray-200/50">
               {cartDetails.map(item => (
                 <div key={item.cart_item_id || item.product_id} className="flex items-start pt-4 first:pt-0">
                   <img
                     src={item.image_url || 'https://via.placeholder.com/80?text=N/A'}
                     alt={item.name}
                     className="h-16 w-16 rounded-lg object-cover mr-4 flex-shrink-0"
                      onError={(e) => { e.target.onerror = null; e.target.src='https://via.placeholder.com/80?text=N/A'; }}
                   />
                   <div className="flex-grow min-w-0">
                     <p className="text-sm font-medium text-gray-800 truncate">{item.name}</p>
                     <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                   </div>
                   <p className="text-sm font-medium text-gray-900 ml-4 flex-shrink-0">
                     ${(parseFloat(item.price || 0) * item.quantity).toFixed(2)}
                   </p>
                 </div>
               ))}
             </div>

             <div className="p-6 border-t border-[var(--vp-border-color)] space-y-3">
               <div className="flex justify-between text-sm">
                 <span className="text-gray-600">Subtotal</span>
                 <span className="font-medium text-gray-900">${subtotal.toFixed(2)}</span>
               </div>
               <div className="flex justify-between text-sm">
                 <span className="text-gray-600">Shipping</span>
                 <span className="font-medium text-gray-900">${shippingCost.toFixed(2)}</span>
               </div>
               <div className="flex justify-between text-sm">
                 <span className="text-gray-600">Taxes</span>
                 <span className="font-medium text-gray-900">${taxes.toFixed(2)}</span>
               </div>
               <div className="flex justify-between text-base font-semibold pt-3 border-t border-dashed border-gray-300 mt-3">
                 <span className="text-gray-900">Total</span>
                 <span className="text-gray-900">${grandTotal.toFixed(2)}</span>
               </div>
             </div>

            <div className="p-6 border-t border-[var(--vp-border-color)]">
                {error && <p className={`${authStyles.errorMessage} text-sm mb-4 bg-red-100 border border-red-300 text-red-800 px-3 py-2 rounded`}>{error}</p>}
                <button
                  type="submit"
                  disabled={!stripe || !elements || isProcessing || !isStripeReady}
                  className={`w-full justify-center rounded-lg py-3 px-4 text-base font-semibold flex items-center gap-2 ${authStyles.primaryButton} bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150 ease-in-out`}
                >
                  {isProcessing ? (
                      <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                             <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                             <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing...
                      </>
                  ) : (
                      <>
                          <LockClosedIcon className="w-5 h-5 mr-1" /> Place Order
                      </>
                  )}
                </button>
            </div>
          </div>
        </div>

      </form>
    </div>
  );
}

export default CheckoutPage;